import Loading from 'quasar/src/plugins/loading/Loading.js';
import TaikyoRoomPane from "src/pages/taikyo/notification/components/TaikyoRoomPane.vue";
import { computed, defineComponent, reactive, ref, toRefs, watch } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import { GetHanyoParam } from "~/api/Common/Commands";
import { DesiredDatePrecautionsResult } from "~/api/data/DesiredDatePrecautionsResult";
import BankSelectField from "~/components/BankSelectField.vue";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { MsgBox } from "~/components/MsgBox";
import RoomSearchInput from "~/components/RoomSearchInput.vue";
import { convertFullToHalf, Log } from "~/extensions/utils";
import { isNullOrUndefined } from "~/extensions/utils/DefineFunction";
import { HanyoKb, PageID } from "~/extensions/utils/DefineTypes";
import { HanyoHelper } from "~/extensions/utils/HanyoHelper";
import { store } from "~/pages/taikyo/store/TaikyoStore";
import { commonStore } from "~/store/CommonStore";
import { myPageStore } from "~/store/MyPageStore";
import { isMatchKeyword } from "../../../extensions/utils/KeywordSearch";
import NotFoundNyukyoDialog from "./components/NotFoundNyukyoDialog.vue";
import TaikyoNotificationCard from "./components/TaikyoNotificationCard.vue";
import TaikyoNotificationList from "./components/TaikyoNotificationList.vue";
import { TaikyoNotificationInputValidator } from "./Models/TaikyoNotificationInputValidator";
import { TaikyoNotificationViewModel } from "./Models/TaikyoNotificationViewModel";
import { useTaikyoTableColumns } from "./Models/useTaikyoTableColumns";
import { api } from "~/api/index";
const log = Log.create("TaikyoNotificationInputPage");
export default defineComponent({
    name: "TaikyoNotificationInputPage",
    components: {
        BankSelectField,
        TaikyoNotificationCard,
        TaikyoNotificationList,
        TaikyoRoomPane,
        BreadcrumbList,
        RoomSearchInput,
        NotFoundNyukyoDialog
    },
    beforeRouteEnter(to, from, next) {
        const fromScreen = from.name;
        const relatedScreens = ["taikyo-precaution", "taikyo-notification-entry-check", "taikyo-room-list"];
        if (relatedScreens.includes(fromScreen) && store.privacyConsent) {
            next();
            return;
        }
        next({ name: "taikyo" });
    },
    async setup() {
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        const isMMA = myPageStore.personal.isMma;
        const router = useRouter();
        const selected = computed(() => store.selectableRooms.filter(n => !taikyo.taikyoList.filter(x => !x.isDeleted).some(x => x.taikyoNo === n.orgNyukyokeiyakuNo)));
        const viewMode = ref("card");
        const lastMode = ref("card");
        const isBankDialog = ref(false);
        const isTmpSave = ref(false);
        const taikyoNotificationComponents = ref([]);
        let desiredDatePrecautions = reactive(new DesiredDatePrecautionsResult());
        const taikyo = reactive(store.taikyoNotificationEntity);
        const viewModel = new TaikyoNotificationViewModel(store.taikyoNotificationEntity, store.taikyoNotificationForInput);
        const valid = reactive(new TaikyoNotificationInputValidator(viewModel));
        const tenants = computed(() => myPageStore.personal.nyukyokeiyakuList.filter(x => store.notFoundNyukyoList.includes(x.orgNyukyokeiyakuNo)));
        // 画面名取得
        const pageName = commonStore.pageName(PageID.taikyoNotificationEntry);
        const registerTaikyoNotificationComponent = (component) => {
            if (component && !taikyoNotificationComponents.value.includes(component)) {
                taikyoNotificationComponents.value.push(component);
            }
        };
        let tab = reactive({ value: "entry" });
        // カラムの表示制御
        const filteredInput = computed(() => store.taikyoNotificationForInput.inputList.filter(x => taikyo.taikyoList.some(y => y.taikyoNo === x.orgNyukyokeiyakuNo && !y.isDeleted)));
        const columns = useTaikyoTableColumns(taikyo, isMultipleKeiyaku, isMMA, filteredInput, {
            showDelete: true,
            showPrecautions: true,
            keyReturnAdrWidth: "400px",
            emailWidth: "480px"
        });
        // 編集後、保存せずルート変更
        onBeforeRouteLeave(async (to, from, next) => {
            // 一時保存実行後の遷移時はスキップ
            if (isTmpSave.value) {
                next();
                return;
            }
            const toScreen = to.name;
            // ログアウト時は別処理で対応
            if (toScreen === "login") {
                next();
                return;
            }
            // 確認画面への遷移時はスキップ
            const relatedScreens = ["taikyo-notification-entry-check"];
            if (relatedScreens.includes(toScreen)) {
                next();
                return;
            }
            // JSONから解約日を外す
            const data = JSON.parse(JSON.stringify(store.taikyoNotificationEntity));
            data.taikyoList.forEach((x) => {
                delete x.kaiyakuYmd;
            });
            const json = JSON.stringify(data);
            // 変更有の場合
            if (store.isSave() && json !== store.storedTaikyoNotificationEntityJson) {
                const isSave = await MsgBox.confirm(commonStore.messages.PI0027);
                if (isSave) {
                    // 保存する場合
                    if (await temporarySaveDialog(true)) {
                        // 保存成功
                        store.clear();
                        next();
                        return;
                    }
                    // 保存失敗
                    next(false);
                    return;
                }
                //保存しない場合
                store.taikyoNotificationEntity.taikyoList.forEach(x => {
                    const fileList = x.fileList;
                    fileList.forEach(y => {
                        y.delFlg = y.newFlg;
                    });
                });
                await store.deleteFile();
                store.clear();
                next();
                return;
            }
            // 申請時に別画面へ遷移した場合
            if (isNullOrUndefined(store.taikyoNotificationEntity.bulkTaikyoNo)) {
                store.taikyoNotificationEntity.taikyoList.forEach(x => {
                    const fileList = x.fileList;
                    fileList.forEach(y => {
                        y.delFlg = true;
                    });
                });
                await store.deleteFile();
            }
            store.clear();
            next();
        });
        // 初期表示処理
        const initView = async () => {
            if (taikyo.taikyoList.length === 0) {
                try {
                    let orgNyukyokeiyakuNoList;
                    if (store.isSave()) {
                        // 一時保存の場合の処理
                        await store.loadTaikyoNotificationSave(taikyo.bulkTaikyoNo);
                        store.selectedRooms = myPageStore.personal.nyukyokeiyakuList.filter(nyukyo => store.taikyoNotificationEntity.taikyoList.some(taikyo => taikyo.taikyoNo === nyukyo.orgNyukyokeiyakuNo));
                        orgNyukyokeiyakuNoList = store.selectedRooms.map(x => x.orgNyukyokeiyakuNo);
                        store.selectableRooms.push(...store.selectedRooms);
                        store.selectableRooms.sort((a, b) => {
                            // 物件名（昇順）
                            const nameCompare = (a.bukkenName ?? "").localeCompare(b.bukkenName ?? "");
                            if (nameCompare !== 0) {
                                return nameCompare;
                            }
                            // ルーム種別（1 → 2）
                            if (a.roomSb !== b.roomSb) {
                                return a.roomSb - b.roomSb;
                            }
                            // 号室（昇順）
                            return Number(a.roomNumber) - Number(b.roomNumber);
                        });
                        createTaikyoList();
                        // 変更有無検知用に初期値をセット
                        store.storedTaikyoNotificationEntityJson = JSON.stringify(store.taikyoNotificationEntity);
                    }
                    else {
                        orgNyukyokeiyakuNoList = store.selectedRooms.map(x => x.orgNyukyokeiyakuNo);
                        await store.loadTaikyoNotification(orgNyukyokeiyakuNoList);
                        createTaikyoList();
                    }
                    await store.loadForTaikyoNotification(orgNyukyokeiyakuNoList, false);
                    desiredDatePrecautions = await api.taikyoNotification.loadDesiredDatePrecautionsText();
                }
                catch (ex) {
                    log.error("apply", ex);
                    MsgBox.error(ex?.message ?? "エラーが発生しました。");
                    router.push({ name: "top" });
                }
            }
        };
        watch(viewMode, val => {
            if (val !== "fullscreen") {
                taikyoNotificationComponents.value = [];
                lastMode.value = viewMode.value;
            }
        });
        // 汎用情報を取得
        const gethanyoParam = [new GetHanyoParam(HanyoKb.KouzaSb), ...(myPageStore.personal.isMma ? [new GetHanyoParam(HanyoKb.MmaInfo)] : [])];
        const hanyoList = await HanyoHelper.getHanyoList(gethanyoParam);
        // MMA情報を取得
        const mmaInfo = hanyoList.GetByHanyoKb(HanyoKb.MmaInfo);
        // 口座種別の取得
        const bankType = hanyoList.GetByHanyoKb(HanyoKb.KouzaSb);
        const bankList = ref([]);
        bankList.value = bankType
            .filter(x => [1, 2].includes(x.value))
            .map(x => ({
            ...x,
            label: x.option1
        }));
        // 初期値設定
        const createTaikyoList = () => {
            taikyo.tantoshaName = taikyo.tantoshaName ? taikyo.tantoshaName : isMMA ? mmaInfo[0].option1 : "";
            taikyo.tantoshaTel = taikyo.tantoshaTel ? taikyo.tantoshaTel : isMMA ? mmaInfo[0].option2 : "";
            taikyo.tantoshaEmail = taikyo.tantoshaEmail ? taikyo.tantoshaEmail : isMMA ? mmaInfo[0].option3 : "";
            taikyo.isKouzaNotRequired = taikyo.isKouzaNotRequired;
            taikyo.henkinBankCd = taikyo.henkinBankCd ?? myPageStore.personal.bankCd;
            taikyo.henkinBankName = taikyo.henkinBankName ?? myPageStore.personal.bankName;
            taikyo.henkinBansCd = taikyo.henkinBansCd ?? myPageStore.personal.bansCd;
            taikyo.henkinBansName = taikyo.henkinBansName ?? myPageStore.personal.bansName;
            taikyo.henkinKouzaSb = taikyo.henkinKouzaSb ?? (myPageStore.personal.kouzaSb === -1 ? 1 : myPageStore.personal.kouzaSb);
            taikyo.henkinKouzaNo = taikyo.henkinKouzaNo ?? (myPageStore.personal.kouzaNo === 0 ? "" : myPageStore.personal.kouzaNo.toString().padStart(7, "0"));
            taikyo.henkinKouzaName = taikyo.henkinKouzaName ?? myPageStore.personal.kouzaName;
            taikyo.henkinKouzaKana = taikyo.henkinKouzaKana ?? myPageStore.personal.kouzaKana;
            taikyo.taikyoList = taikyo.taikyoList.map(room => {
                const nyukyo = myPageStore.personal.nyukyokeiyakuList.find(x => x.orgNyukyokeiyakuNo === room.taikyoNo);
                if (!nyukyo) {
                    return room;
                }
                return {
                    ...room,
                    nyukyokeiyakuNo: nyukyo.nyukyokeiyakuNo,
                    nyukyosha: nyukyo.nyukyosha,
                    bukkenName: nyukyo.bukkenName,
                    roomNumber: nyukyo.roomNumber,
                    shopName: nyukyo.shopName,
                    keyReturnNameKb: store.taikyoNotificationForInput.inputList.find(x => x.orgNyukyokeiyakuNo === nyukyo.orgNyukyokeiyakuNo)?.keyReturnNameKb ?? 0
                };
            });
        };
        // ファイルの削除フラグをfalseで初期化
        store.taikyoNotificationEntity.taikyoList.forEach(x => {
            if (x.fileList) {
                x.fileList.forEach(y => {
                    if (isNullOrUndefined(y.delFlg)) {
                        y.delFlg = false;
                    }
                });
            }
        });
        const addNyukyo = async (selectedNyukyo) => {
            // 削除押下済の入居契約と新規追加する入居契約に分割
            const deletedNyukyo = selectedNyukyo.filter(x => taikyo.taikyoList.some(y => y.taikyoNo === x.orgNyukyokeiyakuNo));
            const addNyukyo = selectedNyukyo.filter(x => !taikyo.taikyoList.some(y => y.taikyoNo === x.orgNyukyokeiyakuNo));
            try {
                Loading.show();
                // 新規追加する入居契約の分のみForInput取得
                const orgNyukyokeiyakuNoList = addNyukyo.map((x) => x.orgNyukyokeiyakuNo);
                if (orgNyukyokeiyakuNoList.length > 0) {
                    await store.loadForTaikyoNotification(orgNyukyokeiyakuNoList, true);
                }
                // selectedRoomsから削除押下済の入居契約を一時的に削除
                store.selectedRooms = store.selectedRooms.filter(x => !deletedNyukyo.some(y => y.orgNyukyokeiyakuNo === x.orgNyukyokeiyakuNo));
                // 削除押下済の入居契約と新規追加する入居契約を一括で追加
                store.selectedRooms.push(...selectedNyukyo.filter(x => !store.notFoundNyukyoList.includes(x.orgNyukyokeiyakuNo)));
                // 削除押下済の退去申請情報の削除フラグをfalseで更新
                taikyo.taikyoList.forEach(x => {
                    if (deletedNyukyo.some(y => y.orgNyukyokeiyakuNo === x.taikyoNo)) {
                        x.isDeleted = false;
                    }
                });
                // 新規追加する入居契約を追加
                taikyo.taikyoList.push(...addNyukyo.map((item) => ({
                    nyukyosha: item.nyukyosha,
                    bukkenName: item.bukkenName,
                    roomNumber: item.roomNumber,
                    shopName: item.shopName,
                    nyukyokeiyakuNo: item.nyukyokeiyakuNo,
                    taikyoNo: item.orgNyukyokeiyakuNo,
                    status: 0,
                    taikyoYmd: null,
                    kaiyakuYmd: null,
                    kaiyakuStrYmd: "",
                    taikyoRiyuKb: null,
                    taikyoRiyuKbName: "",
                    taikyoRiyu: "",
                    sonotaKeiyaku: "",
                    keyReturnName: "",
                    keyReturnNameKb: store.taikyoNotificationForInput.inputList.find(x => x.orgNyukyokeiyakuNo === item.orgNyukyokeiyakuNo)?.keyReturnNameKb ?? 0,
                    keyReturnYmd: null,
                    keyReturnStrYmd: "",
                    keyReturnStrTime: "00:00",
                    numberOfReturnKeys: null,
                    taikyoTachiaiKb: null,
                    taikyoTachiaiKbName: "",
                    taikyoTachiaiName: "",
                    renrakuKb: null,
                    renrakuName: "",
                    renrakuRelationship: "",
                    tel: "",
                    email: "",
                    keyReturnAdrKb: null,
                    soufuName: "",
                    postCode: "",
                    adr1: "",
                    adr2: "",
                    adr3: "",
                    soufuSonota: "",
                    shinseiYmd: null,
                    files: [],
                    fileList: [],
                    isDeleted: false
                })));
                // 退去申請情報リストの並び順をselectedRoomsの並び順に揃える
                const map = new Map(taikyo.taikyoList.map(item => [item.taikyoNo, item]));
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                taikyo.taikyoList = store.selectedRooms.map(x => map.get(x.orgNyukyokeiyakuNo));
            }
            catch (ex) {
                await MsgBox.error(ex.message);
            }
            finally {
                Loading.hide();
            }
        };
        const temporarySaveDialog = async (isLeave = false) => {
            let result = true;
            let isSave = false;
            try {
                const targetFields = ["tantoshaName", "tantoshaTel", "tantoshaEmail"];
                targetFields.forEach(field => {
                    valid[field].modified = true;
                });
                const hasError = targetFields.some(field => valid.errorTo(field));
                if (hasError) {
                    await MsgBox.error("入力に不備があります。<br/>確認してください。");
                    result = false;
                    return result;
                }
                if (!isLeave) {
                    isSave = await MsgBox.confirm(commonStore.messages.PI0029);
                    if (isSave) {
                        isTmpSave.value = true;
                    }
                }
                if (isTmpSave.value || isLeave) {
                    Loading.show();
                    // 登録処理
                    await store.insertSave();
                    await MsgBox.info(commonStore.messages.PI0030);
                    store.clear();
                    if (!isLeave) {
                        router.push({ name: "taikyo-room-list" });
                    }
                }
            }
            catch (ex) {
                await MsgBox.error(ex);
                result = false;
            }
            finally {
                Loading.hide();
            }
            return result;
        };
        const onBankSelect = ({ bank, branch }) => {
            taikyo.henkinBankCd = Number(bank.code);
            taikyo.henkinBansCd = Number(branch.code);
            taikyo.henkinBankName = bank.name;
            taikyo.henkinBansName = branch.name;
        };
        const filter = ref("");
        const customFilter = (rows, terms) => {
            return rows.filter(row => {
                return isMatchKeyword(row.bukkenName, filter.value) || isMatchKeyword(row.roomNumber, filter.value) || isMatchKeyword(row.nyukyosha, filter.value);
            });
        };
        // 確認へボタンの処理
        const submit = async () => {
            let hasError = false;
            taikyoNotificationComponents.value.forEach((component) => {
                const nyukyo = taikyo.taikyoList.find(item => item.taikyoNo == component.$props.row.taikyoNo);
                if (nyukyo?.isDeleted) {
                    return;
                }
                if (component?.runValid?.()) {
                    hasError = true;
                }
            });
            if (valid.error || hasError) {
                MsgBox.error("入力に不備があります。<br/>確認してください。");
                return;
            }
            await router.push({ name: "taikyo-notification-entry-check" });
        };
        const removeNyukyo = async (orgNyukyokeiyakuNo) => {
            const remove = await MsgBox.confirm(commonStore.messages.PI0005);
            if (remove) {
                const target = taikyo.taikyoList.find(x => x.taikyoNo === orgNyukyokeiyakuNo);
                if (target) {
                    target.isDeleted = true;
                }
            }
        };
        const close = () => {
            store.showNotFoundNyukyoDialog = false;
        };
        await initView();
        return {
            ...toRefs(store),
            expansionState: ref(true),
            addNyukyoPane: ref(false),
            temporarySaveDialog,
            myPageStore,
            taikyo,
            valid,
            onBankSelect,
            isBankDialog,
            isMultipleKeiyaku,
            filter,
            columns,
            viewMode,
            customFilter,
            isMMA,
            tab,
            selected,
            submit,
            convertFullToHalf,
            taikyoNotificationComponents,
            registerTaikyoNotificationComponent,
            bankList,
            removeNyukyo,
            addNyukyo,
            lastMode,
            pageName,
            PageID,
            store,
            desiredDatePrecautions,
            tenants,
            close,
            router
        };
    }
});
